

























































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { CourseSection, Day, Timeslot } from "@/typings";
import {
  DAYS,
  getDuration,
  minuteTimeToHour,
  timeslotStartEndUnix,
  toMinutes,
} from "@/utilities";
import { mapGetters } from "vuex";
import SemesterBars from "@/components/SemesterBars.vue";
import { BOverlay } from "bootstrap-vue";

@Component({
  computed: {
    ...mapGetters("settings", ["isMilitaryTime"]),
  },
  components: {
    SemesterBars,
    "b-overlay": BOverlay,
  },
})
export default class Calendar extends Vue {
  @Prop() readonly sections!: CourseSection[];
  @Prop() readonly subsemStartDate!: Date;
  @Prop() readonly subsemEndDate!: Date;
  readonly startTime = 420;
  readonly endTime = 1380;
  readonly totalHeight = 700;

  selectedDate = 0; // This will be set to the correct value before rendering the schedule

  mounted(): void {
    this.resetToStartDate();
  }

  @Watch("sections")
  resetToStartDate(): void {
    this.selectedDate = Number.POSITIVE_INFINITY;

    for (const section of this.sections) {
      for (const timeslot of section.timeslots) {
        const [timeslotStartDate, timeslotEndDate] =
          timeslotStartEndUnix(timeslot);

        if (timeslotStartDate === null || timeslotEndDate === null) {
          continue;
        }

        this.selectedDate = Math.min(this.selectedDate, timeslotStartDate);
      }
    }
  }

  getDays(): Day[] {
    const hasWeekend =
      this.selectedSectionsOnDay({ name: "Saturday", short: "S" }).length +
        this.selectedSectionsOnDay({ name: "Sunday", short: "U" }).length >
      0;

    if (hasWeekend) {
      return DAYS;
    }

    return DAYS.slice(0, 5);
  }

  get numMinutes(): number {
    return this.endTime - this.startTime;
  }

  get dayWidth(): number {
    return 100 / this.getDays().length;
  }

  get hourHeight(): number {
    return (60 * 100) / this.numMinutes;
  }

  get selectedSectionsOnDay() {
    return (day: Day): { section: CourseSection; timeslot: Timeslot }[] => {
      const ret = [];

      for (const section of this.sections) {
        for (const timeslot of section.timeslots) {
          const [timeslotStartDate, timeslotEndDate] =
            timeslotStartEndUnix(timeslot);

          if (
            // Filter for timeslots which include this day
            timeslot.days.includes(day.short) &&
            // Ensure this timeslot is active on the date we're examining
            (timeslotStartDate === null ||
              timeslotEndDate === null ||
              (timeslotStartDate <= this.selectedDate &&
                this.selectedDate <= timeslotEndDate))
          ) {
            ret.push({
              section: section,
              timeslot: timeslot,
            });
          }
        }
      }

      return ret;
    };
  }

  get subsemesterHasSections(): boolean {
    //We dont want to show the "break" overlay if there are no sections in the schedule
    //or if it is the first/last subsemester. This fixes an issue where the "break"
    //overlay flashes on screen
    if (
      this.sections.length === 0 ||
      this.selectedDate === 0 ||
      this.selectedDate === Number.POSITIVE_INFINITY
    ) {
      return true;
    }
    for (const day of this.getDays()) {
      for (const section of this.selectedSectionsOnDay(day)) {
        if (section) {
          return true;
        }
      }
    }
    return false;
  }

  get strHours(): string[] {
    const hours = [];
    for (let time = this.startTime; time < this.endTime; time += 60) {
      hours.push(
        minuteTimeToHour(time, this.$store.getters["settings/isMilitaryTime"]())
      );
    }

    return hours;
  }

  get eventHeight() {
    return (timeslot: Timeslot): number =>
      this.totalHeight * (getDuration(timeslot) / this.numMinutes);
  }

  get eventPosition() {
    return (timeslot: Timeslot): number => {
      const eventStart = toMinutes(timeslot.timeStart);
      return (
        this.totalHeight * ((eventStart - this.startTime) / this.numMinutes)
      );
    };
  }

  get colors() {
    return (crn: number): { [key: string]: string } => {
      const numCalColors = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--num-calendar-colors"
        )
      );
      const colorIdx =
        this.sections.findIndex(
          (section: CourseSection) => section.crn === crn
        ) % numCalColors;

      return {
        bg: "var(--calendar-bg-color-" + colorIdx + ")",
        border: "var(--calendar-border-color-" + colorIdx + ")",
        text: "var(--calendar-text-color-" + colorIdx + ")",
        sbTrack: "var(--calendar-sb-track-color-" + colorIdx + ")",
        sbThumb: "var(--calendar-sb-thumb-color-" + colorIdx + ")",
      };
    };
  }
}
